/**
 * Portfolio Detail Page
 *
 * 포트폴리오 상세 페이지 (NewBotDetailPage 디자인 완전 적용)
 * - TradingBotsLayout과 동일한 Wrapper 구조
 * - 그라데이션 배경, SectionHeader, 6컬럼 스탯 그리드
 *
 * @see NewBotDetailPage.tsx
 */

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconChevronLeft } from '@tabler/icons-react';

import { useCopyPortfolioStore } from '../../hooks/copyTrading/copyPortfolioStore';
import { FollowPortfolioModal } from './modal/FollowPortfolioModal';
import { useMyProfile } from '@/app/hooks/user/myprofile';
import { formatDecimal } from '@utils/formatDecimal';

// 포트폴리오 상태 텍스트
const getStatusText = (status: number, t: (key: string) => string): string => {
  switch (status) {
    case 1:
      return t('active');
    case 2:
      return t('paused');
    case 3:
      return t('closed');
    default:
      return 'Unknown';
  }
};

// 포트폴리오 상태 색상
const getStatusColor = (status: number): string => {
  switch (status) {
    case 1:
      return '#07d993';
    case 2:
      return '#ffcc0d';
    case 3:
      return '#777986';
    default:
      return '#777986';
  }
};

export function PortfolioDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: myProfile } = useMyProfile();
  const { myFollows } = useCopyPortfolioStore();
  const [openFollowModal, setOpenFollowModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'details'>('overview');

  const isMyFollow = (myFollows || []).some(
    (follow) => follow.portfolio.id === Number(id)
  );

  // 이니셜 추출 함수
  const getInitials = (name: string) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  // 이미지 URL 가져오기
  const getPortfolioImageUrl = (): string | null => {
    if (!selectedPortfolio) return null;
    const imageUrl = selectedPortfolio.imageUrl || selectedPortfolio.metadata?.imageUrl || selectedPortfolio.leaderProfileImage || selectedPortfolio.leaderAvatar;
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${import.meta.env.VITE_APP_API_MEDIA_DEV_URL}/${imageUrl}`;
  };

  // Zustand Store
  const {
    selectedPortfolio,
    portfolioStatsMap,
    fetchPortfolioById,
    isLoading,
  } = useCopyPortfolioStore();

  // Effects
  useEffect(() => {
    if (id) {
      fetchPortfolioById(Number(id));
    }
  }, [id]);

  // Handlers
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/copy-trade');
    }
  };

  const handleCopy = () => {
    setOpenFollowModal(true);
  };

  // Loading State
  if (isLoading || !selectedPortfolio) {
    return (
      <Wrapper>
        <Container>
          <LoadingContainer>
            <Spin size="large" />
          </LoadingContainer>
        </Container>
      </Wrapper>
    );
  }

  const portfolio = selectedPortfolio;
  const stats = portfolioStatsMap[portfolio.id];
  const isMyPortfolio = myProfile?.id === portfolio.leaderUserId;
  const isActive = portfolio.status === 1;
  const isPaused = portfolio.status === 2;

  // ROI formatting
  const formatROI = (value: number | undefined) => {
    if (value === undefined || value === null) return '-';
    const prefix = value >= 0 ? '+' : '';
    return `${prefix}${formatDecimal(value, 2)}%`;
  };

  // PnL formatting
  const formatPnL = (value: number | undefined) => {
    if (value === undefined || value === null) return '-';
    const prefix = value >= 0 ? '+' : '';
    return `${prefix}${formatDecimal(value, 2)} USDT`;
  };

  return (
    <>
      {/* Follow Modal */}
      <FollowPortfolioModal
        open={openFollowModal}
        setOpen={setOpenFollowModal}
        portfolio={portfolio}
      />

      <Wrapper>
        <Container>
          {/* Section Header */}
          <SectionHeader>
            <BackButton onClick={handleBack}>
              <IconChevronLeft size={24} color="#ffffff" />
            </BackButton>
            <SectionLabel>{t('copyTradingPage.tagLabel')}</SectionLabel>
            <HeaderSpacer />
          </SectionHeader>

          {/* Content */}
          <Content>
            {/* Header Actions */}
            <Header>
              <HeaderActions>
                {!isMyPortfolio && (
                  <>
                    {isActive && !isMyFollow && (
                      <ActionButton $variant="primary" onClick={handleCopy}>
                        {t('copyTradingPage.copy')}
                      </ActionButton>
                    )}
                    {isMyFollow && (
                      <ActionButton onClick={() => navigate('/copy-trade/my-follows')}>
                        {t('copyTradingPage.manageFollow')}
                      </ActionButton>
                    )}
                  </>
                )}
              </HeaderActions>
            </Header>

            {/* Portfolio Info Card */}
            <PortfolioInfoCard>
              <PortfolioHeader>
                <PortfolioIconWrapper>
                  {getPortfolioImageUrl() ? (
                    <PortfolioImage
                      src={getPortfolioImageUrl()!}
                      alt={portfolio.portfolioName}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <PortfolioIconFallback className={getPortfolioImageUrl() ? 'hidden' : ''}>
                    {getInitials(portfolio.portfolioName || portfolio.leaderUsername)}
                  </PortfolioIconFallback>
                </PortfolioIconWrapper>
                <PortfolioDetails>
                  <PortfolioName>{portfolio.portfolioName}</PortfolioName>
                  <PortfolioMeta>
                    <span>{portfolio.leaderUsername}</span>
                    <span>•</span>
                    <span>{t('copyTradingPage.copyTrading')}</span>
                    <span>•</span>
                    <span>{portfolio.currentFollowers} {t('copyTradingPage.followers')}</span>
                  </PortfolioMeta>
                </PortfolioDetails>
                <StatusBadge $color={getStatusColor(portfolio.status)}>
                  {getStatusText(portfolio.status, t)}
                </StatusBadge>
              </PortfolioHeader>

              {/* Leader Info */}
              <LeaderMeta>
                <MetaItem>
                  <MetaLabel>{t('copyTradingPage.leader')}</MetaLabel>
                  <MetaValue>{portfolio.leaderUsername}</MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>{t('profitShareRate')}</MetaLabel>
                  <MetaValue>{formatDecimal(portfolio.profitShareRate, 2)}%</MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>{t('minCopyAmount')}</MetaLabel>
                  <MetaValue>{formatDecimal(portfolio.minCopyAmount, 2)} USDT</MetaValue>
                </MetaItem>
              </LeaderMeta>

              {/* Desktop Copy Button - 데스크톱용 카피 버튼 */}
              {!isMyPortfolio && (
                <DesktopCopyButtonWrapper>
                  <DesktopCopyButton
                    onClick={handleCopy}
                    disabled={!isActive || isMyFollow}
                    $isFollowing={isMyFollow}
                  >
                    {isPaused
                      ? t('paused')
                      : isActive
                        ? isMyFollow
                          ? t('copyTradingPage.following')
                          : t('copyTradingPage.copy')
                        : t('closed')}
                  </DesktopCopyButton>
                  {isMyFollow && (
                    <ManageFollowLink onClick={() => navigate('/copy-trade/my-follows')}>
                      {t('copyTradingPage.manageFollow')} →
                    </ManageFollowLink>
                  )}
                </DesktopCopyButtonWrapper>
              )}
            </PortfolioInfoCard>

            {/* Stats Grid */}
            <StatsGrid>
              <StatCard>
                <StatLabel>{t('copyTradingPage.totalPnL')}</StatLabel>
                <StatValue $positive={(portfolio.totalPnl || 0) >= 0}>
                  {formatPnL(portfolio.totalPnl)}
                </StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>{t('roi30d')}</StatLabel>
                <StatValue $positive={(stats?.roi30d || 0) >= 0}>
                  {formatROI(stats?.roi30d)}
                </StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>{t('winRate')}</StatLabel>
                <StatValue>
                  {stats?.winRate ? `${formatDecimal(stats.winRate, 1)}%` : '-'}
                </StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>{t('totalTrades')}</StatLabel>
                <StatValue>
                  {stats?.totalTrades?.toLocaleString() || '-'}
                </StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>{t('currentFollowers')}</StatLabel>
                <StatValue>{portfolio.currentFollowers.toLocaleString()}</StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>{t('maxDrawdown')}</StatLabel>
                <StatValue $positive={false}>
                  {stats?.maxDrawdown ? `-${formatDecimal(stats.maxDrawdown, 2)}%` : '-'}
                </StatValue>
              </StatCard>
            </StatsGrid>

            {/* Tab Navigation */}
            <TabContainer>
              <TabItem $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
                {t('tradingBotPage.overview')}
              </TabItem>
              <TabItem $active={activeTab === 'details'} onClick={() => setActiveTab('details')}>
                {t('portfolioDetails')}
              </TabItem>
            </TabContainer>

            {/* Tab Content */}
            <TabContent>
              {activeTab === 'overview' && (
                <OverviewContent>
                  {/* Performance Section */}
                  <ConfigSection>
                    <SectionTitle>{t('copyTradingPage.performance')}</SectionTitle>
                    <ConfigGrid>
                      <ConfigItem>
                        <ConfigLabel>{t('roi30d')}</ConfigLabel>
                        <ConfigValue $positive={(stats?.roi30d || 0) >= 0}>
                          {formatROI(stats?.roi30d)}
                        </ConfigValue>
                      </ConfigItem>
                      <ConfigItem>
                        <ConfigLabel>{t('roi90d')}</ConfigLabel>
                        <ConfigValue $positive={(stats?.roi90d || 0) >= 0}>
                          {formatROI(stats?.roi90d)}
                        </ConfigValue>
                      </ConfigItem>
                      <ConfigItem>
                        <ConfigLabel>{t('winRate')}</ConfigLabel>
                        <ConfigValue>
                          {stats?.winRate ? `${formatDecimal(stats.winRate, 1)}%` : '-'}
                        </ConfigValue>
                      </ConfigItem>
                      <ConfigItem>
                        <ConfigLabel>{t('sharpeRatio')}</ConfigLabel>
                        <ConfigValue>
                          {stats?.sharpeRatio ? formatDecimal(stats.sharpeRatio, 2) : '-'}
                        </ConfigValue>
                      </ConfigItem>
                    </ConfigGrid>
                  </ConfigSection>

                  {/* Risk Warning */}
                  <WarningSection>
                    <WarningIcon>⚠️</WarningIcon>
                    <WarningContent>
                      <WarningTitle>{t('investmentRiskWarning')}</WarningTitle>
                      <WarningText>{t('copyTradingRiskDescription')}</WarningText>
                    </WarningContent>
                  </WarningSection>
                </OverviewContent>
              )}

              {activeTab === 'details' && (
                <OverviewContent>
                  <ConfigSection>
                    <SectionTitle>{t('portfolioDetails')}</SectionTitle>
                    <ConfigGrid>
                      <ConfigItem>
                        <ConfigLabel>{t('profitShareRate')}</ConfigLabel>
                        <ConfigValue $highlight>
                          {formatDecimal(portfolio.profitShareRate, 2)}%
                        </ConfigValue>
                      </ConfigItem>
                      <ConfigItem>
                        <ConfigLabel>{t('minCopyAmount')}</ConfigLabel>
                        <ConfigValue>
                          {formatDecimal(portfolio.minCopyAmount, 2)} USDT
                        </ConfigValue>
                      </ConfigItem>
                      <ConfigItem>
                        <ConfigLabel>{t('maxFollowers')}</ConfigLabel>
                        <ConfigValue>
                          {portfolio.maxFollowers.toLocaleString()}
                        </ConfigValue>
                      </ConfigItem>
                      <ConfigItem>
                        <ConfigLabel>{t('currentFollowers')}</ConfigLabel>
                        <ConfigValue>
                          {portfolio.currentFollowers.toLocaleString()}
                        </ConfigValue>
                      </ConfigItem>
                      <ConfigItem>
                        <ConfigLabel>{t('createdDate')}</ConfigLabel>
                        <ConfigValue>
                          {new Date(portfolio.createdAt).toLocaleDateString()}
                        </ConfigValue>
                      </ConfigItem>
                      <ConfigItem>
                        <ConfigLabel>{t('lastUpdated')}</ConfigLabel>
                        <ConfigValue>
                          {new Date(portfolio.updatedAt).toLocaleDateString()}
                        </ConfigValue>
                      </ConfigItem>
                    </ConfigGrid>
                  </ConfigSection>
                </OverviewContent>
              )}
            </TabContent>
          </Content>
        </Container>

        {/* Mobile Copy Button */}
        {!isMyPortfolio && (
          <MobileCopyButtonWrapper>
            <MobileCopyButton
              onClick={handleCopy}
              disabled={!isActive || isMyFollow}
              $isFollowing={isMyFollow}
            >
              {isPaused
                ? t('paused')
                : isActive
                  ? isMyFollow
                    ? t('copyTradingPage.following')
                    : t('copyTradingPage.copy')
                  : t('closed')}
            </MobileCopyButton>
          </MobileCopyButtonWrapper>
        )}
      </Wrapper>
    </>
  );
}

// ==========================================
// Styled Components - TradingBotsLayout 구조 동일 적용
// ==========================================

// 전체 페이지 래퍼 (그라데이션 배경)
const Wrapper = styled.div`
  box-sizing: border-box;
  padding-top: 84px;
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(180deg, #0a1321 0%, #0f231f 100%);

  @media (max-width: 768px) {
    padding-top: 60px;
  }
`;

// 컨테이너
const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px 24px 0 24px;
  gap: 20px;

  @media (max-width: 768px) {
    padding: 8px 16px 0 16px;
    gap: 12px;
  }
`;

// 섹션 헤더 (뒤로가기 + 중앙 타이틀)
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  height: 70px;
  margin: 0;
  padding: 0 4px;
  background: transparent;

  @media (max-width: 768px) {
    height: 50px;
    margin: 0 -16px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;

  &:active {
    opacity: 0.7;
  }
`;

const SectionLabel = styled.span`
  font-family: 'Aggro', sans-serif;
  font-size: 42px;
  font-weight: 500;
  color: #ffffff;
  letter-spacing: -0.84px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  @media (max-width: 768px) {
    font-family: 'NanumSquareRound', sans-serif;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.32px;
  }
`;

const HeaderSpacer = styled.div`
  width: 44px;
`;

// 콘텐츠 영역
const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 60px;

  @media (max-width: 768px) {
    padding-bottom: 100px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'danger' }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  ${(props) => {
    switch (props.$variant) {
      case 'primary':
        return `
          color: #0d1117;
          background: #07d993;
          border: none;
          &:hover { opacity: 0.9; }
        `;
      case 'danger':
        return `
          color: #ff4d4f;
          background: transparent;
          border: 1px solid #ff4d4f;
          &:hover { background: rgba(255, 77, 79, 0.1); }
        `;
      default:
        return `
          color: #07d993;
          background: transparent;
          border: 1px solid #07d993;
          &:hover { background: rgba(7, 217, 147, 0.1); }
        `;
    }
  }}
`;

const PortfolioInfoCard = styled.div`
  background: rgba(0, 110, 73, 0.08);
  border: 1px solid rgba(7, 217, 147, 0.1);
  border-radius: 24px;
  padding: 32px;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(7, 217, 147, 0.2);
  }

  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 16px;
  }
`;

const PortfolioHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 480px) {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const PortfolioIconWrapper = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 20px;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 8px 24px rgba(7, 217, 147, 0.3);

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    border-radius: 12px;
  }
`;

const PortfolioImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PortfolioIconFallback = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #07d993 0%, #00a878 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 32px;
  font-weight: bold;

  &.hidden {
    display: none;
  }

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const PortfolioDetails = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: 480px) {
    flex: unset;
    width: calc(100% - 60px);
  }
`;

const PortfolioName = styled.h1`
  font-family: 'Aggro', sans-serif;
  font-size: 28px;
  font-weight: 500;
  color: #ffffff;
  margin: 0 0 8px 0;
  word-break: break-word;

  @media (max-width: 480px) {
    font-size: 18px;
    margin: 0 0 4px 0;
  }
`;

const PortfolioMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #9496a1;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    font-size: 12px;
    gap: 4px;
  }
`;

const StatusBadge = styled.span<{ $color: string }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => props.$color};
  background: ${(props) => `${props.$color}15`};
  padding: 8px 16px;
  border-radius: 20px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    font-size: 12px;
    padding: 6px 12px;
    order: -1;
    margin-left: auto;
  }
`;

const LeaderMeta = styled.div`
  display: flex;
  gap: 32px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 480px) {
    gap: 16px;
    flex-wrap: wrap;
  }
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetaLabel = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  color: #9496a1;
`;

const MetaValue = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

// Desktop Copy Button Styles
const DesktopCopyButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    display: none; /* 모바일에서는 하단 고정 버튼 사용 */
  }
`;

const DesktopCopyButton = styled.button<{
  disabled?: boolean;
  $isFollowing?: boolean;
}>`
  padding: 16px 48px;
  border-radius: 16px;
  border: ${({ $isFollowing }) =>
    $isFollowing ? '1px solid #07d993' : 'none'};
  background: ${({ $isFollowing, disabled }) =>
    disabled ? '#333' : $isFollowing ? 'transparent' : 'linear-gradient(135deg, #07d993 0%, #05c584 100%)'};
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${({ $isFollowing, disabled }) =>
    disabled ? '#666' : $isFollowing ? '#07d993' : '#0d1117'};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  box-shadow: ${({ disabled, $isFollowing }) =>
    disabled || $isFollowing ? 'none' : '0 4px 16px rgba(7, 217, 147, 0.3)'};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ $isFollowing }) =>
      $isFollowing ? 'none' : '0 6px 20px rgba(7, 217, 147, 0.4)'};
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

const ManageFollowLink = styled.button`
  background: transparent;
  border: none;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #9496a1;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s ease;

  &:hover {
    color: #07d993;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 20px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  background: rgba(0, 110, 73, 0.08);
  border: 1px solid rgba(7, 217, 147, 0.08);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 110, 73, 0.12);
    border-color: rgba(7, 217, 147, 0.15);
    transform: translateY(-2px);
  }

  @media (max-width: 480px) {
    padding: 12px;
    gap: 6px;
  }
`;

const StatLabel = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #9496a1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.span<{ $positive?: boolean }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: ${(props) => (props.$positive === undefined ? '#ffffff' : props.$positive ? '#07d993' : '#ff4d4f')};

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const TabItem = styled.button<{ $active?: boolean }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: ${(props) => (props.$active ? '700' : '400')};
  color: ${(props) => (props.$active ? '#07d993' : '#777986')};
  background: transparent;
  border: none;
  padding: 8px 16px;
  cursor: pointer;
  border-bottom: 2px solid ${(props) => (props.$active ? '#07d993' : 'transparent')};
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    color: #07d993;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    padding: 8px 12px;
  }
`;

const TabContent = styled.div`
  background: rgba(0, 110, 73, 0.08);
  border: 1px solid rgba(7, 217, 147, 0.08);
  border-radius: 20px;
  padding: 32px;

  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 12px;
  }
`;

const OverviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ConfigSection = styled.div``;

const SectionTitle = styled.h3`
  font-family: 'Aggro', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #ffffff;
  margin: 0 0 16px 0;
`;

const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  padding: 20px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    gap: 12px;
    padding: 12px;
  }
`;

const ConfigItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: center;
`;

const ConfigLabel = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #9496a1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ConfigValue = styled.span<{ $positive?: boolean; $highlight?: boolean }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${({ $positive, $highlight }) =>
    $highlight ? '#ffcc0d' : $positive === false ? '#ff4d4f' : $positive ? '#07d993' : '#ffffff'};
`;

const WarningSection = styled.div`
  display: flex;
  gap: 16px;
  padding: 20px 24px;
  background: rgba(255, 204, 13, 0.08);
  border: 1px solid rgba(255, 204, 13, 0.3);
  border-radius: 16px;

  @media (max-width: 768px) {
    padding: 16px 20px;
  }
`;

const WarningIcon = styled.span`
  font-size: 24px;
  flex-shrink: 0;
`;

const WarningContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const WarningTitle = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffcc0d;
`;

const WarningText = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #9496a1;
  margin: 0;
  line-height: 1.6;
`;

const MobileCopyButtonWrapper = styled.div`
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 20px;
  background: linear-gradient(to top, #0d1117 80%, transparent);
  z-index: 100;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileCopyButton = styled.button<{
  disabled?: boolean;
  $isFollowing?: boolean;
}>`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: ${({ $isFollowing }) =>
    $isFollowing ? '1px solid #07d993' : 'none'};
  background: ${({ $isFollowing, disabled }) =>
    disabled ? '#333' : $isFollowing ? 'transparent' : 'linear-gradient(135deg, #07d993 0%, #05c584 100%)'};
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ $isFollowing, disabled }) =>
    disabled ? '#666' : $isFollowing ? '#07d993' : '#0d1117'};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  box-shadow: ${({ disabled, $isFollowing }) =>
    disabled || $isFollowing ? 'none' : '0 4px 16px rgba(7, 217, 147, 0.3)'};

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  width: 100%;
`;

export default PortfolioDetailPage;
